/**
 * Interactive Map with Leaflet.js for Shopify
 * Recreates MapPress functionality with OpenStreetMap
 */

// Prevent redeclaration if already loaded
if (typeof window.InteractiveMap === 'undefined') {

class InteractiveMap {
  constructor(containerId, options = {}) {
    this.containerId = containerId;
    this.map = null;
    this.markers = [];
    this.options = {
      center: [52.5, 5.7], // Default to Netherlands center
      zoom: 11,
      minZoom: 1,
      maxZoom: 18,
      scrollWheelZoom: true,
      zoomControl: true,
      attributionControl: true,
      ...options
    };
    
    this.init();
  }

  init() {
    // Check if Leaflet is loaded
    if (typeof L === 'undefined') {
      this.loadLeaflet(() => this.initializeMap());
    } else {
      this.initializeMap();
    }
  }

  loadLeaflet(callback) {
    // Load Leaflet CSS
    const cssLink = document.createElement('link');
    cssLink.rel = 'stylesheet';
    cssLink.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    cssLink.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
    cssLink.crossOrigin = '';
    document.head.appendChild(cssLink);

    // Load Leaflet JS
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
    script.crossOrigin = '';
    script.onload = callback;
    document.head.appendChild(script);
  }

  initializeMap() {
    const container = document.getElementById(this.containerId);
    if (!container) {
      console.error(`Map container with id "${this.containerId}" not found`);
      return;
    }

    // Show loading state
    container.innerHTML = '<div class="map-loading">Loading map...</div>';

    // Initialize the map
    this.map = L.map(this.containerId, {
      center: this.options.center,
      zoom: this.options.zoom,
      minZoom: this.options.minZoom,
      maxZoom: this.options.maxZoom,
      scrollWheelZoom: this.options.scrollWheelZoom,
      zoomControl: this.options.zoomControl,
      attributionControl: false // We'll add custom attribution
    });

    // Add OpenStreetMap tiles (same as MapPress uses)
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 18,
    }).addTo(this.map);

    // Add custom attribution
    if (this.options.attributionControl) {
      L.control.attribution({
        prefix: '<a href="https://leafletjs.com" title="A JavaScript library for interactive maps">Leaflet</a>'
      }).addAttribution('© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors').addTo(this.map);
    }

    // Load markers if provided
    if (this.options.markers) {
      this.addMarkers(this.options.markers);
    }

    // Emit ready event
    this.onMapReady();
  }

  async addMarkers(markersData = []) {
    if (!Array.isArray(markersData) || markersData.length === 0) {
      return;
    }

    // Process all markers properly handling async lookups
    const predictions = markersData.map(markerData => this.addMarker(markerData));
    await Promise.allSettled(predictions);

    const markerCount = this.markers.length;

    // Fit map to show all markers if more than one
    if (markerCount > 1) {
      const group = new L.featureGroup(this.markers);
      this.map.fitBounds(group.getBounds().pad(0.1));
    } else if (markerCount === 1) {
      // Center on single marker
      this.map.setView(this.markers[0].getLatLng(), this.options.zoom);
    }
  }

  async addMarker(markerData) {
    let { lat, lng, title, description, description1, iconUrl, addressString } = markerData;

    let latNum = typeof lat === 'string' ? parseFloat(lat) : lat;
    let lngNum = typeof lng === 'string' ? parseFloat(lng) : lng;

    // Check if we need to geocode
    if ((!Number.isFinite(latNum) || !Number.isFinite(lngNum)) && addressString) {
      try {
        const coords = await this.geocodeAddress(addressString);
        if (coords) {
          latNum = parseFloat(coords.lat);
          lngNum = parseFloat(coords.lon);
          console.log(`Geocoded '${addressString}' to:`, latNum, lngNum);
        }
      } catch (e) {

        console.warn('Geocoding failed for', addressString, e);
      }
    }

    if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) {
      console.warn('Skipping marker with invalid coordinates:', markerData);
      return null;
    }

    let markerIcon;
    if (iconUrl) {
      markerIcon = L.icon({
        iconUrl: iconUrl,
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32]
      });
    } else {
      // Use modern SVG pin
      markerIcon = this.createDefaultIcon();
    }

    const marker = L.marker([latNum, lngNum], { icon: markerIcon }).addTo(this.map);

    const popupParts = [];
    if (title) {
      popupParts.push(`<h4 style="margin: 0 0 8px 0; font-size: 16px; font-weight: bold;">${title}</h4>`);
    }
    // if (description) {
    //   popupParts.push(`<p style="margin: 0 0 8px 0; font-size: 14px; line-height: 1.4;">${description}</p>`);
    // }
    if (description1) {
      popupParts.push(`<div class="popup-scroll-block" style="margin: 0 0 8px 0; font-size: 14px; line-height: 1.4;">${description1}</div>`);
    }

    const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${latNum},${lngNum}`;
    popupParts.push(`<p class="get-direction-div-block" style="margin: 0; font-size: 14px;"><a href="${directionsUrl}" target="_blank" rel="noopener noreferrer">Get directions</a></p>`);

    marker.bindPopup(popupParts.join(''));

    this.markers.push(marker);
    return marker;
  }

  async geocodeAddress(query) {
    try {
      // Using Nominatim OpenStreetMap API
      const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`;
      const response = await fetch(url, {
        headers: { 'Accept': 'application/json' } 
      });
      if (!response.ok) return null;
      const data = await response.json();
      return data && data.length > 0 ? data[0] : null;
    } catch (error) {
      console.error('Error during geocoding:', error);
      return null;
    }
  }

  createDefaultIcon() {
    // Modern SVG Pin Icon
    const svgPin = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="36" height="36" style="filter: drop-shadow(0 3px 4px rgba(0,0,0,0.4));">
      <path fill="#e74c3c" stroke="#c0392b" stroke-width="1" d="M12 0C7.58 0 4 3.58 4 8c0 5.25 8 16 8 16s8-10.75 8-16c0-4.42-3.58-8-8-8zm0 11c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z"/>
      <circle cx="12" cy="8" r="3.5" fill="white"/>
    </svg>`;
    
    return L.divIcon({
      className: 'custom-marker-pin',
      html: svgPin,
      iconSize: [36, 36],
      iconAnchor: [18, 36],
      popupAnchor: [0, -40]
    });
  }

  // Method to add a single marker programmatically
  addSingleMarker(lat, lng, options = {}) {
    const markerData = {
      lat: lat,
      lng: lng,
      title: options.title || '',
      description: options.description || '',
      description1: options.description1 || '',
      iconUrl: options.iconUrl || null
    };
    return this.addMarker(markerData);
  }

  // Method to clear all markers
  clearMarkers() {
    this.markers.forEach(marker => {
      this.map.removeLayer(marker);
    });
    this.markers = [];
  }

  // Method to fit map to markers
  fitToMarkers() {
    if (this.markers.length > 0) {
      const group = new L.featureGroup(this.markers);
      this.map.fitBounds(group.getBounds().pad(0.1));
    }
  }

  // Method to set map view
  setView(lat, lng, zoom) {
    if (this.map) {
      this.map.setView([lat, lng], zoom || this.options.zoom);
    }
  }

  onMapReady() {
    // Dispatch custom event when map is ready
    const event = new CustomEvent('mapReady', { 
      detail: { 
        map: this.map, 
        instance: this 
      } 
    });
    document.dispatchEvent(event);
  }

  // Public method to get the Leaflet map instance
  getMap() {
    return this.map;
  }

  // Method to destroy the map
  destroy() {
    if (this.map) {
      this.map.remove();
      this.map = null;
      this.markers = [];
    }
  }
}

// Maps data cache
let mapsDataCache = null;
let mapsDataUrl = null;

// Function to load predefined maps data
async function loadMapsData(preferredUrl) {
  if (preferredUrl) {
    mapsDataUrl = preferredUrl;
  }
  if (mapsDataCache) return mapsDataCache;
  
  try {
    if (!mapsDataUrl) {
      if (typeof window !== 'undefined' && window.__interactiveMapsDataUrl) {
        mapsDataUrl = window.__interactiveMapsDataUrl;
      } else if (typeof window !== 'undefined' && window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
        mapsDataUrl = `${window.Shopify.routes.root}assets/maps-data.json`;
      } else {
        mapsDataUrl = '/assets/maps-data.json';
      }
    }

    let response = await fetch(mapsDataUrl);

    if (!response.ok && mapsDataUrl !== '/assets/maps-data.json') {
      response = await fetch('/assets/maps-data.json');
    }

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    mapsDataCache = await response.json();
    
    // Log available map IDs for debugging
    const availableIds = Object.keys(mapsDataCache);
    console.log('Available map IDs:', availableIds);
    
    return mapsDataCache;
  } catch (error) {
    console.error('Error loading maps data:', error);
    return null;
  }
}

// Function to get predefined map data
function getPredefinedMapData(mapId) {
  if (!mapsDataCache || !mapsDataCache[mapId]) {
    console.error(`Map with ID ${mapId} not found in predefined data`);
    return null;
  }
  
  const mapData = mapsDataCache[mapId];
  
  // Transform markers to expected format
  const markers = mapData.markers.map(marker => ({
    lat: marker.lat,
    lng: marker.lng,
    title: marker.title,
    description: marker.body || marker.address || '',
    description1: marker.body || marker.address || '',
    iconUrl: marker.icon
  }));
  
  return {
    title: mapData.title,
    markers: markers
  };
}

// Initialize maps when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  // Auto-initialize maps with data attributes
  const mapContainers = document.querySelectorAll('[data-interactive-map]');
  
  mapContainers.forEach(async (container) => {
    const mapId = container.id;
    if (!mapId) {
      console.warn('Map container must have an id attribute');
      return;
    }

    if (container.dataset.mapsDataUrl && !window.__interactiveMapsDataUrl) {
      window.__interactiveMapsDataUrl = container.dataset.mapsDataUrl;
    }

    // Check if this is a predefined map
    if (container.dataset.usePredefined === 'true' && container.dataset.mapId) {
      await initializePredefinedMap(container);
    } else {
      initializeCustomMap(container);
    }
  });
});

// Initialize predefined map
async function initializePredefinedMap(container) {
  const mapId = container.id;
  const predefinedMapId = container.dataset.mapId;
  const dataUrl = container.dataset.mapsDataUrl;
  if (!window.__interactiveMapsDataUrl && dataUrl) {
    window.__interactiveMapsDataUrl = dataUrl;
  }
  
  // Load maps data first
  await loadMapsData(dataUrl);
  
  // Get predefined map data
  const mapData = getPredefinedMapData(predefinedMapId);
  if (!mapData) {
    console.error(`Failed to load predefined map ${predefinedMapId}`);
    container.innerHTML = '<div class="map-loading">Error: Map not found</div>';
    return;
  }
  
  // Initialize map with predefined data
  const options = {
    markers: mapData.markers
  };
  
  const map = new InteractiveMap(mapId, options);
  
  // Update legend if it exists
  const legendContainer = document.querySelector(`[data-map-legend="${predefinedMapId}"]`);
  if (legendContainer) {
    updateLegend(legendContainer, mapData);
  }
}

function extractMarkersFromContainer(container) {
  if (!container) {
    return [];
  }

  let rawMarkers = [];
  const markersAttr = container.dataset.markers;

  if (markersAttr) {
    try {
      rawMarkers = JSON.parse(markersAttr);
    } catch (error) {
      try {
        rawMarkers = JSON.parse(markersAttr.replace(/&quot;/g, '"'));
      } catch (attrError) {
        console.error('Error parsing markers from data attribute:', attrError);
        rawMarkers = [];
      }
    }
  }

  if (!Array.isArray(rawMarkers) || rawMarkers.length === 0) {
    const markersScript = container.querySelector('script[type="application/json"]');
    if (markersScript) {
      try {
        rawMarkers = JSON.parse(markersScript.textContent);
      } catch (error) {
        console.error('Error parsing markers from script tag:', error);
        rawMarkers = [];
      }
    }
  }

  if (!Array.isArray(rawMarkers)) {
    rawMarkers = [];
  }

  return rawMarkers
    .map(marker => {
      const latValue = typeof marker.lat === 'string' ? parseFloat(marker.lat) : marker.lat;
      const lngValue = typeof marker.lng === 'string' ? parseFloat(marker.lng) : marker.lng;

      if (!Number.isFinite(latValue) || !Number.isFinite(lngValue)) {
        return null;
      }

      return {
        lat: latValue,
        lng: lngValue,
        title: marker.title || '',
        addressString: marker.addressString || '',
        description: marker.description || marker.body || marker.address || '',
        description1: marker.description1 || marker.body || marker.address || '',
        iconUrl: marker.iconUrl || marker.icon || null
      };
    })
    .filter(Boolean);
}

// Initialize custom map (existing functionality)
function initializeCustomMap(container) {
  const mapId = container.id;
  
  // Parse options from data attributes
  const options = {};
  
  if (container.dataset.mapCenter) {
    const [lat, lng] = container.dataset.mapCenter.split(',').map(value => parseFloat(value));
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      options.center = [lat, lng];
    }
  }
  
  if (container.dataset.mapZoom) {
    const zoomLevel = parseInt(container.dataset.mapZoom, 10);
    if (!Number.isNaN(zoomLevel)) {
      options.zoom = zoomLevel;
    }
  }

  const markers = extractMarkersFromContainer(container);

  if (markers.length > 0) {
    options.markers = markers;
  }

  // Initialize the map
  new InteractiveMap(mapId, options);
}

// Function to update legend
function updateLegend(legendContainer, mapData) {
  const legendContent = legendContainer.querySelector('.legend-content');
  if (!legendContent) return;
  
  let legendHTML = '';
  mapData.markers.forEach(marker => {
    if (marker.title) {
      legendHTML += `
        <div class="legend-item">
          <div class="legend-marker" style="background-color: #e74c3c;"></div>
          <span>${marker.title}</span>
        </div>
      `;
    }
  });
  
  legendContent.innerHTML = legendHTML;
}

// Make InteractiveMap globally available
window.InteractiveMap = InteractiveMap;

} // End of redeclaration prevention
